import { size, has, isEmpty, isArray } from 'lodash';
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { FixedSizeList, VariableSizeList } from 'react-window';

import Message2, { MessageType } from '../Message2';
import InfiniteList from '../../../InfiniteList';
import { Print, Arrow, EditField, Forward } from '@icons/common';
import OnClickOutside from '../../../OnClickOutside';
import Messenger, { Types } from '../Messenger';
import { MessageObjectType } from '@components/CRM/Tickets/types';
import { IMessage2 } from '../Message2/Message2';
import CopyOnClick from '@components/CRM/Common/CopyOnClick';
import ExcessTicketHistory from '../ExcessTicketHistory';
import { ExcessTicketType } from '@components/CRM/Tickets/types';

const getItemKey = (index, data) => {
  if (!data || !has(data, `${index}.id`)) {
    return index;
  }

  return data[index].id;
};

export const StyledMessages = styled(InfiniteList)`
  position: relative;
`;

export const StyledMessage = styled(Message2)`
  border-radius: 0;
  width: 100%;
`;

export const sortConversationItems = items =>
  [...items].sort((firstItem, secondItem) => {
    const firstTime = firstItem.excessTicket
      ? firstItem.excessTicket.chain?.messages[0]?.message.time ||
        firstItem.excessTicket.createdAt
      : firstItem.message.time;
    const secondTime = secondItem.excessTicket
      ? secondItem.excessTicket.chain?.messages[0]?.message.time ||
        secondItem.excessTicket.createdAt
      : secondItem.message.time;

    return firstTime - secondTime;
  });

const Conversation2 = ({
  messages,
  historyMessages,
  subject,
  hasNextPage,
  isNextPageLoading,
  onLoadRequest,
  onLoadHistoryMessages,
  onRevealServiceMessages,
  className,
  isDisableChangeSubject,
  onChangeSubject,
  unreadMessages,
  sentMessagesCounter,
  isCreate,
  printMessageHref,
  rootTicketId,
  rootChainId,
  type,
  chatId,
  subAgentName,
  onForwardMessage,
  onForwardChain,
  isClosedTicket,
  excessTickets = [],
}: IConversation2) => {
  const [isHistoryConversation, setIsHistoryConversation] = useState(false);
  const [subjectValue, setSubjectValue] = useState(subject);
  const [isEditable, setIsEditable] = useState(false);
  const [isHeaderShadow, setIsHeaderShadow] = useState(true);
  const [isGotoBottomShow, setIsGotoBottomShow] = useState(false);
  const [expandedExcessTicketIds, setExpandedExcessTicketIds] = useState<
    string[]
  >([]);

  const listRef = useRef<FixedSizeList | VariableSizeList>();

  const isListScrolledToBottom = useCallback(() => {
    const outerElement = listRef.current?._outerRef;

    if (!outerElement) {
      return false;
    }

    return (
      outerElement.scrollHeight <= outerElement.clientHeight ||
      outerElement.scrollTop + outerElement.clientHeight >=
        outerElement.scrollHeight - 1
    );
  }, []);

  const syncGotoBottomVisibility = useCallback(
    (isLastMessageVisible = false) => {
      setIsGotoBottomShow(
        !isLastMessageVisible && !isListScrolledToBottom()
      );
    },
    [isListScrolledToBottom]
  );

  const allMessages = useMemo(() => {
    const excessTicketItems = excessTickets
      .filter(({ chain }) => chain)
      .map(ticket => ({
        excessTicket: ticket,
        isExpanded: expandedExcessTicketIds.includes(ticket.id),
      }));

    const conversationMessages = isArray(historyMessages)
      ? [...historyMessages, ...messages]
      : messages;

    return sortConversationItems([
      ...conversationMessages,
      ...excessTicketItems,
    ]);
  }, [messages, historyMessages, excessTickets, expandedExcessTicketIds]);

  const handleToggleExcessTicket = useCallback((ticketId: string) => {
    setExpandedExcessTicketIds(ids =>
      ids.includes(ticketId)
        ? ids.filter(id => id !== ticketId)
        : [...ids, ticketId]
    );
  }, []);

  const handleScrollMessages = useCallback(
    event => {
      setIsHeaderShadow(event.scrollOffset > 30);

      if (event.scrollUpdateWasRequested) {
        setIsGotoBottomShow(false);
        return;
      }

      syncGotoBottomVisibility();
    },
    [syncGotoBottomVisibility]
  );

  const isItemLoaded = useCallback(
    index => !hasNextPage || index < allMessages.length - 1,
    [hasNextPage, allMessages]
  );

  const handleToggleEditable = () => {
    setIsEditable(prevIsEditable => !prevIsEditable);
  };

  const handleLoadRequest = () => {
    if (isNextPageLoading || !hasNextPage) {
      return null;
    }

    return onLoadRequest();
  };

  const handleChangeSubject = event => {
    const { value } = event.target;
    setSubjectValue(value);
    onChangeSubject(value);
  };

  const handleClickGotoBottom = useCallback(() => {
    const lastMessageIndex = size(allMessages) - 1;
    setIsGotoBottomShow(false);
    listRef?.current?.scrollToItem(lastMessageIndex, 'end');
  }, [allMessages]);

  const handleScrollBottomBorder = () => {
    setIsGotoBottomShow(false);
  };

  const renderMessages = useCallback(
    ({ data, index, style }) => {
      if (!data || !data[index]) {
        return null;
      }

      if (data[index].excessTicket) {
        return (
          <div style={style}>
            <ExcessTicketHistory
              ticket={data[index].excessTicket}
              isExpanded={data[index].isExpanded}
              onToggle={handleToggleExcessTicket}
            />
          </div>
        );
      }

      const { client, message } = data[index];

      return (
        <StyledMessage
          isLast={!historyMessages && index === 0}
          isEmail={type === Types.MAIL}
          rootTicketId={rootTicketId}
          rootChainId={rootChainId}
          onLoadHistoryMessages={onLoadHistoryMessages}
          onRevealServiceMessages={onRevealServiceMessages}
          key={message.id}
          client={client}
          data-id={message.id}
          data-read={message.isRead}
          data-auto={message.isAuto}
          data-delivered={message.isDelivered}
          data-incoming={message.isIncoming}
          data-hidden={message.isHidden}
          message={message}
          style={style}
          onForwardMessage={onForwardMessage}
          isClosedTicket={isClosedTicket}
        />
      );
    },
    [messages, historyMessages, rootChainId, handleToggleExcessTicket]
  );

  const handleHistoryConversationStart = useCallback(
    renderProps => {
      const isLastMessageVisible =
        renderProps.visibleStopIndex >= size(allMessages) - 1;

      syncGotoBottomVisibility(isLastMessageVisible);

      if (
        !isHistoryConversation &&
        listRef.current &&
        !isEmpty(historyMessages)
      ) {
        const lastMessagesIndex = size(allMessages) - size(messages);
        listRef.current.scrollToItem(lastMessagesIndex);
        setIsHistoryConversation(true);
      }

      if (hasNextPage) {
        const { height: innerHeight } = renderProps;
        const containerHeight = listRef?.current?._outerRef?.clientHeight;

        if (containerHeight > innerHeight) onLoadRequest();
      }
    },
    [
      listRef?.current?.height,
      isHistoryConversation,
      allMessages,
      historyMessages,
      messages,
      hasNextPage,
      syncGotoBottomVisibility,
    ]
  );

  useEffect(() => {
    setSubjectValue(subject);
  }, [subject]);

  useEffect(() => {
    if (listRef.current && sentMessagesCounter > 0) {
      const lastMessageIndex = size(allMessages) - 1;
      listRef.current.scrollToItem(lastMessageIndex, 'start');
    }
  }, [sentMessagesCounter, allMessages]);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      syncGotoBottomVisibility();
    });

    return () => cancelAnimationFrame(animationFrame);
  }, [allMessages, syncGotoBottomVisibility]);

  return (
    <StyledConversation className={className}>
      <StyledHeader isHeaderShadow={isHeaderShadow}>
        <StyledHeaderContainer>
          {isEditable || isCreate ? (
            <StyledOnClickOutside onClickOutside={handleToggleEditable}>
              <StyledTextAreaSubject
                placeholder="Укажите тему"
                onChange={handleChangeSubject}
                value={subjectValue}
              />
            </StyledOnClickOutside>
          ) : (
            <StyledSubject>
              <StyledMessenger type={type} />
              {subjectValue}
              {chatId && type === Types.TELEGRAM && (
                <StyledCopyOnClick
                  valueForCopy={chatId}
                  hintText="Скопировать ID чата"
                >
                  <span>{chatId}</span>
                </StyledCopyOnClick>
              )}
              {!isDisableChangeSubject && isCreate && (
                <StyledEditField onClick={handleToggleEditable}>
                  <StyledEditFieldIcon />
                </StyledEditField>
              )}
            </StyledSubject>
          )}
          <a href={printMessageHref} target="_blank" rel="noreferrer">
            <StyledPrintIcon />
          </a>
        </StyledHeaderContainer>
        {Boolean(subAgentName) && (
          <StyledSubAgentName $hasPaddingLeft={!(isEditable || isCreate)}>
            {subAgentName}
          </StyledSubAgentName>
        )}
      </StyledHeader>
      {!isCreate ? (
        <StyledContainer>
          {!isEmpty(allMessages) && (
            <StyledMessages
              flexible
              isReverse
              isNextPageLoading={isNextPageLoading}
              itemCount={size(allMessages)}
              isItemLoaded={isItemLoaded}
              onLoadRequest={handleLoadRequest}
              onScrollBottomBorder={handleScrollBottomBorder}
              onScroll={handleScrollMessages}
              itemData={allMessages}
              itemKey={getItemKey}
              onItemsRendered={handleHistoryConversationStart}
              ref={listRef}
            >
              {renderMessages}
            </StyledMessages>
          )}
          <StyledGotoBottomButton
            isShow={isGotoBottomShow}
            onClick={handleClickGotoBottom}
          >
            {unreadMessages > 0 && (
              <StyledUnreadMessagesCount>
                {unreadMessages}
              </StyledUnreadMessagesCount>
            )}
            <StyledArrowIcon unreadMessages={unreadMessages} />
          </StyledGotoBottomButton>
          {type === Types.MAIL && !isClosedTicket && (
            <StyledForwardChainButton
              onClick={() => onForwardChain(true)}
              isShownGotoBottomButton={isGotoBottomShow}
            >
              <span>Переслать целиком</span>
              <StyledForwardIcon />
            </StyledForwardChainButton>
          )}
        </StyledContainer>
      ) : (
        <StyledNoFound>
          <StyledHead>Укажите тему и получателя</StyledHead>
          <StyledBody>
            Укажите тему и получателя
            <br />и отправьте сообщение
          </StyledBody>
        </StyledNoFound>
      )}
    </StyledConversation>
  );
};

export interface IConversation2 {
  className?: string;
  hasNextPage: boolean;
  historyMessages: MessageObjectType[] | null;
  isClosedTicket: boolean;
  isCreate: boolean;
  isDisableChangeSubject: boolean;
  isForwardingChain?: boolean;
  isNextPageLoading: boolean;
  messages: MessageObjectType[];
  excessTickets?: ExcessTicketType[];
  messengers: string[] | null;
  minimumBatchSize: number;
  rootChainId: string | null;
  rootTicketId: string | null;
  sentMessagesCounter: number;
  subAgentName?: string | null;
  subject: string;
  total: number;
  type: string;
  chatId: string | null;
  unreadMessages: number;
  printMessageHref: string;

  onChangeSubject: (value: string) => void;
  onForwardChain: (isForwarding: boolean) => void;
  onForwardMessage: IMessage2['onForwardMessage'];
  onLoadHistoryMessages: IMessage2['onLoadHistoryMessages'];
  onLoadRequest: () => void;
  onRevealServiceMessages: IMessage2['onRevealServiceMessages'];
}

Conversation2.defaultProps = {
  className: '',
  messages: [],
  historyMessages: null,
  hasNextPage: true,
  isNextPageLoading: false,
  subject: null,
  onLoadRequest: Function.prototype,
  onChangeSubject: Function.prototype,
  onRevealServiceMessages: Function.prototype,
  isDisableChangeSubject: false,
  unreadMessages: 0,
  sentMessagesCounter: 0,
  isCreate: false,
  onClick: Function.prototype,
  type: null,
  printMessageHref: '',
  rootTicketId: null,
  rootChainId: null,
  onLoadHistoryMessages: Function.prototype,
};

const StyledCopyOnClick = styled(CopyOnClick)`
  margin-left: 10px;

  span {
    font-size: 18px;
    color: #17181a;
  }
`;

const StyledConversation = styled.div`
  position: relative;
  border-radius: 4px;
  box-shadow: 0 10px 15px 0 rgba(196, 194, 182, 0.9),
    0 1px 2px 0 rgba(210, 208, 197, 0.9);
  width: 100%;
  height: 100%;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const StyledHeader = styled.div<{ isHeaderShadow: boolean }>`
  min-height: 22px;
  padding: 11px 10px 9px 10px;
  position: relative;

  &:after {
    display: ${({ isHeaderShadow }) => (isHeaderShadow ? 'block' : 'none')};
    content: '';
    position: absolute;
    left: 0;
    top: 100%;
    right: 0;
    height: 59px;
    background: linear-gradient(
      to bottom,
      #fff,
      rgba(255, 255, 255, 0.6),
      rgba(255, 255, 255, 0)
    );
    z-index: 1;
  }
`;

const StyledContainer = styled.div`
  flex-grow: 2;
`;

export const StyledEditField = styled.span`
  position: relative;
`;

const StyledEditFieldIcon = styled(EditField)`
  width: 16px;
  height: 15px;
  position: relative;
  left: 4px;
`;

const StyledArrowIcon = styled(({ ...props }) => <Arrow {...props} />)`
  width: 20px;
  height: 19px;
  margin-top: ${({ unreadMessages }) => (unreadMessages > 0 ? '15px' : '12px')};

  path {
    stroke: ${({ unreadMessages }) =>
      unreadMessages > 0 ? '#007AFF' : '#919499'};
  }
`;

const StyledOnClickOutside = styled(OnClickOutside)`
  width: 100%;
`;

export const StyledTextAreaSubject = styled.textarea`
  width: 100%;
  height: 100%;
  font-size: 18px;
  font-family: 'Backpack', sans-serif;
  font-weight: bold;
  line-height: 22px;
  color: #17181a;
  border: 0;
  resize: none;

  &::placeholder {
    font-weight: normal;
    color: #919499;
  }
`;

const StyledHeaderContainer = styled.div`
  display: flex;
`;

const StyledSubject = styled.div`
  width: 100%;
  height: 100%;
  font-size: 18px;
  font-weight: bold;
  line-height: 22px;
  color: #17181a;
  border: 0;
  display: flex;
  align-items: start;
`;

const StyledMessenger = styled(Messenger)`
  min-height: 16px;
  width: 16px;
  margin-right: 6px;
  margin-top: 2px;
`;

const StyledNoFound = styled.div`
  align-items: center;
  border: 1px dashed #919499;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
`;

const StyledHead = styled.div`
  color: #17181a;
  font-size: 22px;
  font-weight: 700;
`;

const StyledBody = styled.div`
  color: #4d4d4d;
  line-height: 19px;
  text-align: center;

  ${StyledHead} + & {
    margin-top: 15px;
  }
`;

const StyledGotoBottomButton = styled.button<{ isShow: boolean }>`
  position: absolute;
  display: ${({ isShow }) => (isShow ? 'flex' : 'none')};
  justify-content: center;
  box-shadow: 0 2px 10px rgba(23, 24, 26, 0.15);
  bottom: 8px;
  right: 8px;
  width: 40px;
  height: 40px;
  border-radius: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  background: #ffffff;
`;

const StyledForwardIcon = styled(Forward)`
  width: 20px;
  height: 20px;
  transition: all 0.5s;
`;

const StyledForwardChainButton = styled.button<{
  isShownGotoBottomButton: boolean;
}>`
  @keyframes slideSpan {
    from {
      right: 100%;
    }
    to {
      right: 35px;
    }
  }

  position: absolute;
  display: flex;
  justify-content: right;
  align-items: center;
  box-shadow: 0 2px 10px rgba(23, 24, 26, 0.15);
  bottom: 8px;
  right: ${({ isShownGotoBottomButton }) =>
    isShownGotoBottomButton ? '68px' : '8px'};
  width: 40px;
  height: 40px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  background: #ffffff;
  transition: all 0.5s;
  overflow: hidden;
  padding: 0 10px;

  & span {
    white-space: nowrap;
    opacity: 0;
    font-size: 12px;
    position: absolute;
    left: -120px;
    left: ${({ isShownGotoBottomButton }) =>
      isShownGotoBottomButton ? '-180px' : '-120px'};
    transition: left 0.5s, opacity 0.5s;
  }

  &:hover {
    width: 155px;
    border-radius: 20px;
    padding: 0px 10px;
  }

  &:hover ${StyledForwardIcon} {
    width: 20px;
    height: 20px;
    margin-left: auto;
  }

  &:hover span {
    opacity: 1;
    left: 10px;
    animation: slideSpan 0.5s forwards;
  }
`;

const StyledUnreadMessagesCount = styled.div`
  position: absolute;
  top: 8px;
  background: white;
  font-size: 12px;
  font-weight: 700;
  color: #007aff;
`;

const StyledPrintIcon = styled(Print)`
  width: 22px;
  height: 19px;
  cursor: pointer;
  width: 25px;
  margin-left: 5px;
  margin-top: 5px;
`;

const StyledSubAgentName = styled.div<{ $hasPaddingLeft: boolean }>`
  font-size: 14px;
  font-weight: 700;
  color: #4b137b;
  margin-top: 5px;
  ${({ $hasPaddingLeft }) => $hasPaddingLeft && 'padding-left: 22px;'}
`;

export default Conversation2;
